import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { Star, TrendingUp, TrendingDown, Minus, Loader2, SmilePlus, Meh, Frown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

interface NpsRating {
  id: string;
  order_id: string;
  restaurant_id: string;
  customer_id: string;
  nps_score: number;
  created_at: string;
}

const tooltipStyle = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
  fontSize: "11px",
  color: "hsl(var(--foreground))",
};

const NPS_COLORS = {
  promoter: "hsl(142, 71%, 45%)",
  passive: "hsl(45, 93%, 47%)",
  detractor: "hsl(0, 72%, 51%)",
};

interface Props {
  restaurants: any[];
}

const AdminNpsTab = ({ restaurants }: Props) => {
  const [ratings, setRatings] = useState<NpsRating[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from("platform_nps_ratings" as any)
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);
      setRatings((data as any[]) || []);
      setLoading(false);
    };
    fetch();
  }, []);

  const stats = useMemo(() => {
    if (ratings.length === 0) return null;

    const promoters = ratings.filter(r => r.nps_score >= 9).length;
    const passives = ratings.filter(r => r.nps_score >= 7 && r.nps_score <= 8).length;
    const detractors = ratings.filter(r => r.nps_score <= 6).length;
    const total = ratings.length;
    const npsScore = Math.round(((promoters - detractors) / total) * 100);

    // Distribution
    const distribution = Array.from({ length: 11 }, (_, i) => ({
      score: i,
      count: ratings.filter(r => r.nps_score === i).length,
      fill: i >= 9 ? NPS_COLORS.promoter : i >= 7 ? NPS_COLORS.passive : NPS_COLORS.detractor,
    }));

    // Per restaurant
    const byRestaurant = new Map<string, { scores: number[]; name: string }>();
    ratings.forEach(r => {
      if (!byRestaurant.has(r.restaurant_id)) {
        const rest = restaurants.find(res => res.id === r.restaurant_id);
        byRestaurant.set(r.restaurant_id, { scores: [], name: rest?.name || "Desconhecido" });
      }
      byRestaurant.get(r.restaurant_id)!.scores.push(r.nps_score);
    });

    const restaurantNps = Array.from(byRestaurant.entries()).map(([id, data]) => {
      const p = data.scores.filter(s => s >= 9).length;
      const d = data.scores.filter(s => s <= 6).length;
      const n = data.scores.length;
      return { id, name: data.name, nps: Math.round(((p - d) / n) * 100), total: n };
    }).sort((a, b) => b.nps - a.nps);

    // Monthly trend
    const monthly = new Map<string, number[]>();
    ratings.forEach(r => {
      const key = new Date(r.created_at).toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
      if (!monthly.has(key)) monthly.set(key, []);
      monthly.get(key)!.push(r.nps_score);
    });
    const trend = Array.from(monthly.entries()).map(([month, scores]) => {
      const p = scores.filter(s => s >= 9).length;
      const d = scores.filter(s => s <= 6).length;
      return { month, nps: Math.round(((p - d) / scores.length) * 100), count: scores.length };
    }).reverse().slice(-6);

    const pieData = [
      { name: "Promotores", value: promoters, color: NPS_COLORS.promoter },
      { name: "Passivos", value: passives, color: NPS_COLORS.passive },
      { name: "Detratores", value: detractors, color: NPS_COLORS.detractor },
    ];

    return { npsScore, promoters, passives, detractors, total, distribution, restaurantNps, trend, pieData };
  }, [ratings, restaurants]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-20">
        <SmilePlus className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
        <p className="text-sm text-muted-foreground">Nenhuma avaliação NPS recebida ainda.</p>
        <p className="text-xs text-muted-foreground mt-1">As avaliações aparecerão quando clientes avaliarem o ViaMenu.</p>
      </div>
    );
  }

  const npsColor = stats.npsScore >= 50 ? NPS_COLORS.promoter : stats.npsScore >= 0 ? NPS_COLORS.passive : NPS_COLORS.detractor;
  const npsIcon = stats.npsScore >= 50 ? SmilePlus : stats.npsScore >= 0 ? Meh : Frown;
  const NpsIcon = npsIcon;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-bold text-lg">NPS — Avaliação do ViaMenu</h2>
        <p className="text-xs text-muted-foreground mt-0.5">Net Promoter Score coletado dos clientes após avaliação de pedido</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 text-center">
          <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: `${npsColor}15` }}>
            <NpsIcon className="h-6 w-6" style={{ color: npsColor }} />
          </div>
          <p className="text-3xl font-bold font-display" style={{ color: npsColor }}>{stats.npsScore}</p>
          <p className="text-[10px] text-muted-foreground mt-1">NPS Score</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 text-center">
          <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center bg-[hsl(142,71%,45%)]/10">
            <TrendingUp className="h-6 w-6 text-[hsl(142,71%,45%)]" />
          </div>
          <p className="text-3xl font-bold font-display text-[hsl(142,71%,45%)]">{stats.promoters}</p>
          <p className="text-[10px] text-muted-foreground mt-1">Promotores (9-10)</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 text-center">
          <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center bg-[hsl(45,93%,47%)]/10">
            <Minus className="h-6 w-6 text-[hsl(45,93%,47%)]" />
          </div>
          <p className="text-3xl font-bold font-display text-[hsl(45,93%,47%)]">{stats.passives}</p>
          <p className="text-[10px] text-muted-foreground mt-1">Passivos (7-8)</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="rounded-2xl border border-border/60 bg-card shadow-sm p-5 text-center">
          <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center bg-[hsl(0,72%,51%)]/10">
            <TrendingDown className="h-6 w-6 text-[hsl(0,72%,51%)]" />
          </div>
          <p className="text-3xl font-bold font-display text-[hsl(0,72%,51%)]">{stats.detractors}</p>
          <p className="text-[10px] text-muted-foreground mt-1">Detratores (0-6)</p>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Score Distribution */}
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5">
          <h3 className="font-display font-semibold text-sm mb-4">Distribuição de Notas</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stats.distribution}>
              <XAxis dataKey="score" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="count" name="Respostas" radius={[6, 6, 0, 0]}>
                {stats.distribution.map((d, i) => (
                  <Cell key={i} fill={d.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie chart */}
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5">
          <h3 className="font-display font-semibold text-sm mb-4">Classificação</h3>
          <div className="flex items-center gap-6">
            <ResponsiveContainer width={160} height={160}>
              <PieChart>
                <Pie data={stats.pieData} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={70} strokeWidth={2}>
                  {stats.pieData.map((d, i) => (
                    <Cell key={i} fill={d.color} stroke="hsl(var(--card))" />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-3 flex-1">
              {stats.pieData.map((d) => (
                <div key={d.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="text-xs text-muted-foreground">{d.name}</span>
                  </div>
                  <span className="text-xs font-bold">{d.value} ({stats.total > 0 ? Math.round((d.value / stats.total) * 100) : 0}%)</span>
                </div>
              ))}
              <div className="pt-2 border-t border-border/50 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Total</span>
                <span className="text-xs font-bold">{stats.total}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* NPS by Restaurant */}
      {stats.restaurantNps.length > 0 && (
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm p-5">
          <h3 className="font-display font-semibold text-sm mb-4">NPS por Restaurante</h3>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {stats.restaurantNps.map((r) => {
              const color = r.nps >= 50 ? NPS_COLORS.promoter : r.nps >= 0 ? NPS_COLORS.passive : NPS_COLORS.detractor;
              return (
                <div key={r.id} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{r.name}</p>
                    <p className="text-[10px] text-muted-foreground">{r.total} avaliação{r.total !== 1 ? "ões" : ""}</p>
                  </div>
                  <div className="flex-1">
                    <div className="h-2 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(5, ((r.nps + 100) / 200) * 100)}%`, backgroundColor: color }} />
                    </div>
                  </div>
                  <span className="text-sm font-bold w-12 text-right" style={{ color }}>{r.nps}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNpsTab;